/*! blogutility-cold-runtime.js v1.0.2
   Cold-Weather Emergency Lights — Runtime Estimator
   - Non-transactional guidance tool (no product links)
   - Namespaced, idempotent, accessible (labels + aria-live)
   - Mounts on any <div data-cold-runtime-tool></div>
*/
(function(){
  "use strict";

  const NS = "cwrt";
  const SELECTOR = "[data-cold-runtime-tool]";
  if (window.__CW_RT_INIT__) return;
  window.__CW_RT_INIT__ = true;

  // Quoted temperature keys (including negatives)
  const DERATE = {
    "Ni-Cd":   { "77":1.00, "50":0.90, "32":0.80, "14":0.70, "-4":0.60, "-22":0.45 },
    "SLA":     { "77":1.00, "50":0.80, "32":0.65, "14":0.50, "-4":0.40, "-22":0.25 },
    "LiFePO4": { "77":1.00, "50":0.85, "32":0.70, "14":0.50, "-4":0.30, "-22":0.20 }
  };

  const styles = `
  .${NS}-wrap{font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Inter,Arial;color:#0f172a}
  .${NS}-card{border:1px solid #e5e7eb;border-radius:12px;padding:16px;background:#fff}
  .${NS}-header{display:flex;align-items:center;gap:.5rem;margin:0 0 8px;font-weight:700}
  .${NS}-header .${NS}-icon{color:#0ea5e9}
  .${NS}-sub{margin:0 0 12px;color:#475569;font-size:14px}
  .${NS}-form{display:grid;gap:12px;grid-template-columns:1fr}
  .${NS}-row{display:grid;gap:10px;grid-template-columns:1fr minmax(220px,260px)}
  .${NS}-row label{font-weight:600}
  .${NS}-row .${NS}-field{justify-self:end}
  .${NS}-hint{color:#64748b;font-size:12px;margin-top:4px}
  .${NS}-actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:8px}
  .${NS}-btn{border:1px solid #cbd5e1;border-radius:999px;background:#0f172a;color:#fff;padding:.5rem .8rem;font-weight:600;font-size:14px;cursor:pointer}
  .${NS}-btn.secondary{background:#fff;color:#0f172a}
  .${NS}-btn:focus{outline:2px solid #2563eb;outline-offset:2px}
  .${NS}-results{margin-top:14px;border-top:1px dashed #e5e7eb;padding-top:14px}
  .${NS}-pill{display:inline-flex;align-items:center;gap:.4rem;padding:.35rem .6rem;border-radius:999px;border:1px solid #cbd5e1;background:#f8fafc;color:#0f172a;font-size:12px;margin:2px}
  .${NS}-list{margin:10px 0 0;padding-left:18px}
  .${NS}-muted{color:#64748b}
  .${NS}-table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px;color:#334155}
  .${NS}-table th,.${NS}-table td{border:1px solid #e5e7eb;padding:6px 8px;text-align:center}
  .${NS}-sr{position:absolute!important;height:1px;width:1px;overflow:hidden;clip:rect(1px,1px,1px,1px);white-space:nowrap}
  @media (max-width:720px){ .${NS}-row{grid-template-columns:1fr} .${NS}-row .${NS}-field{justify-self:start} }
  `;

  function injectCSS(){
    if (document.getElementById(NS+"-styles")) return;
    const s = document.createElement("style");
    s.id = NS+"-styles";
    s.textContent = styles;
    document.head.appendChild(s);
  }

  function el(tag, cls, html){
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function derateFactor(chem, tempF){
    const map = DERATE[chem] || DERATE["Ni-Cd"];
    const keys = Object.keys(map).map(k=>parseFloat(k)).sort((a,b)=>a-b);
    let factor = keys.length ? map[String(keys[keys.length-1])] : 1;
    for (let i=0;i<keys.length;i++){
      const k = keys[i];
      if (tempF <= k){ factor = map[String(k)]; break; }
    }
    return factor;
  }

  function build(root){
    const wrap = el("div", NS+"-wrap");
    const card = el("div", NS+"-card");
    wrap.appendChild(card);

    const h = el("h3", NS+"-header", `<span class="fa-solid fa-temperature-low ${NS}-icon" aria-hidden="true"></span> Cold-Weather Runtime Estimator`);
    const sub = el("p", NS+"-sub", 'Estimate runtime at your lowest ambient using simple derating factors by chemistry. Guidance only—verify against manufacturer data and AHJ requirements.');
    card.appendChild(h);
    card.appendChild(sub);

    const form = el("form", NS+"-form");
    form.setAttribute("novalidate","true");
    form.innerHTML = `
      <div class="${NS}-row">
        <label for="${NS}-temp">Lowest ambient temperature (°F)</label>
        <div class="${NS}-field">
          <input id="${NS}-temp" type="number" step="1" value="20" style="width:120px" aria-label="Lowest ambient Fahrenheit">
          <div class="${NS}-hint">Below 32°F: expect significant capacity loss; heaters may be required.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-chem">Battery chemistry</label>
        <div class="${NS}-field">
          <select id="${NS}-chem" aria-label="Battery chemistry">
            <option>Ni-Cd</option>
            <option>SLA</option>
            <option>LiFePO4</option>
          </select>
          <div class="${NS}-hint">Choose what the fixture uses (see spec sheet).</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-pubmin">Published runtime at 77°F (minutes)</label>
        <div class="${NS}-field">
          <input id="${NS}-pubmin" type="number" step="1" value="90" style="width:120px" aria-label="Published runtime at 77°F">
          <div class="${NS}-hint">UL 924 minimum is 90 minutes.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-power">Published emergency power at 77°F (W)</label>
        <div class="${NS}-field">
          <input id="${NS}-power" type="number" step="0.1" value="12" style="width:120px" aria-label="Published emergency power at 77°F in watts">
          <div class="${NS}-hint">Fixture’s 90-min power budget (heads + legend). For remotes, include remote load.</div>
        </div>
      </div>

      <div class="${NS}-row">
        <label for="${NS}-load">Your expected load (W)</label>
        <div class="${NS}-field">
          <input id="${NS}-load" type="number" step="0.1" value="12" style="width:120px" aria-label="Expected load in watts">
          <div class="${NS}-hint">Heads + legend + any remotes you plan to drive.</div>
        </div>
      </div>
    `;

    // Actions row
    const actionsEl = el("div", NS+"-actions");
    const btn = el("button", NS+"-btn", '<span class="fa-solid fa-calculator" aria-hidden="true"></span> Estimate Runtime');
    btn.type = "button";
    const reset = el("button", NS+"-btn secondary", '<span class="fa-solid fa-rotate" aria-hidden="true"></span> Reset');
    reset.type = "button";
    actionsEl.appendChild(btn);
    actionsEl.appendChild(reset);

    const result = el("div", NS+"-results");
    result.setAttribute("aria-live", "polite");
    result.innerHTML = `<p class="${NS}-muted">Enter your site conditions and click “Estimate Runtime.”</p>`;

    const tbl = el("table", NS+"-table");
    tbl.innerHTML = `
      <thead><tr><th>°F</th><th>Ni-Cd</th><th>SLA</th><th>LiFePO4</th></tr></thead>
      <tbody>
        <tr><td>77</td><td>1.00</td><td>1.00</td><td>1.00</td></tr>
        <tr><td>50</td><td>0.90</td><td>0.80</td><td>0.85</td></tr>
        <tr><td>32</td><td>0.80</td><td>0.65</td><td>0.70</td></tr>
        <tr><td>14</td><td>0.70</td><td>0.50</td><td>0.50</td></tr>
        <tr><td>-4</td><td>0.60</td><td>0.40</td><td>0.30</td></tr>
        <tr><td>-22</td><td>0.45</td><td>0.25</td><td>0.20</td></tr>
      </tbody>
    `;

    card.appendChild(form);
    card.appendChild(actionsEl);
    card.appendChild(result);
    card.appendChild(el("div", null, '<div class="'+NS+'-hint">Rules of thumb only. Derating varies by cell design, charger, and enclosure temperature.</div>'));
    card.appendChild(tbl);

    function compute(){
      const temp = parseFloat(form.querySelector('#'+NS+'-temp').value || "20");
      const chem = form.querySelector('#'+NS+'-chem').value;
      const pubMin = Math.max(1, parseFloat(form.querySelector('#'+NS+'-pubmin').value || "90"));
      const pubPower = Math.max(0.1, parseFloat(form.querySelector('#'+NS+'-power').value || "12"));
      const load = Math.max(0.1, parseFloat(form.querySelector('#'+NS+'-load').value || "12"));

      const factor = derateFactor(chem, temp);
      const wh77 = pubPower * (pubMin/60);
      const whCold = wh77 * factor;
      const runtimeMin = (whCold / load) * 60;

      const pass = runtimeMin >= 90;
      const shortfall = Math.max(0, 90 - runtimeMin);

      const pills = [
        `<span class="${NS}-pill"><span class="fa-solid fa-temperature-arrow-down" aria-hidden="true"></span> ${temp.toFixed(0)}°F</span>`,
        `<span class="${NS}-pill"><span class="fa-solid fa-battery-half" aria-hidden="true"></span> ${chem}</span>`,
        `<span class="${NS}-pill"><span class="fa-solid fa-gauge" aria-hidden="true"></span> Derate ×${factor.toFixed(2)}</span>`
      ].join(" ");

      let html = `<div>${pills}</div>`;
      html += `<ul class="${NS}-list">
        <li><strong>Estimated runtime at ${temp.toFixed(0)}°F:</strong> ${runtimeMin.toFixed(0)} minutes ${pass? '(<span class="'+NS+'-muted">meets ≥90 min</span>)' : '— <strong style="color:#b91c1c">short by ~'+shortfall.toFixed(0)+' min</strong>'}</li>
        <li><strong>Basis:</strong> Published ${pubPower} W for ${pubMin} min (≈ ${(wh77).toFixed(1)} Wh at 77°F), derated to ${(whCold).toFixed(1)} Wh at ${temp.toFixed(0)}°F.</li>
        <li><strong>Load assumed:</strong> ${load} W (heads + legend ${load>pubPower?'+ remotes':''}).</li>
      </ul>`;

      const recs = [];
      if (!pass){
        if (temp <= 32) recs.push("Specify a cold-weather/heater option to recover runtime.");
        if (temp <= -4) recs.push("Consider locating the battery indoors with outdoor-rated remote heads.");
        recs.push("Reduce load (lower-watt heads) or choose a higher emergency power model.");
      } else {
        if (temp <= 32) recs.push("Maintain heater kits where provided; verify charger performance in winter.");
        recs.push("Night-aim and record light levels at the door + 10–20 ft down-path.");
      }
      if (recs.length){
        html += `<p><strong>Recommendations:</strong> ${recs.join(" ")}</p>`;
      }

      const summaryTxt = [
        `Lowest ambient: ${temp.toFixed(0)}°F`,
        `Chemistry: ${chem}, derate x${factor.toFixed(2)}`,
        `Published: ${pubPower} W for ${pubMin} min (~${wh77.toFixed(1)} Wh)`,
        `Load: ${load} W`,
        `Estimated runtime: ${runtimeMin.toFixed(0)} min ${pass? '(meets 90 min)':'(shortfall ~'+shortfall.toFixed(0)+' min)'}`,
        recs.length? 'Recommendations: '+recs.join(' '): null
      ].filter(Boolean).join('\n');

      html += `<div class="${NS}-actions" style="margin-top:10px">
        <button type="button" class="${NS}-btn secondary" data-copy="${NS}"><span class="fa-solid fa-clipboard" aria-hidden="true"></span> Copy Summary</button>
        <button type="button" class="${NS}-btn secondary" data-reset="${NS}"><span class="fa-solid fa-rotate" aria-hidden="true"></span> Clear</button>
      </div>
      <div class="${NS}-sr" aria-live="polite">Estimate ready</div>`;

      result.innerHTML = html;

      const copyBtn = result.querySelector('[data-copy="'+NS+'"]');
      const clearBtn = result.querySelector('[data-reset="'+NS+'"]');
      if (copyBtn){
        copyBtn.addEventListener("click", ()=>{
          navigator.clipboard.writeText(summaryTxt).catch(()=>{});
          copyBtn.textContent = "Copied!";
          setTimeout(()=>{ copyBtn.textContent = "Copy Summary"; }, 1300);
        });
      }
      if (clearBtn){
        clearBtn.addEventListener("click", ()=>{
          result.innerHTML = `<p class="${NS}-muted">Enter your site conditions and click “Estimate Runtime.”</p>`;
        });
      }
    }

    // Wire up actions
    btn.addEventListener("click", compute);
    reset.addEventListener("click", function(){
      form.reset();
      form.querySelector('#'+NS+'-temp').value = "20";
      form.querySelector('#'+NS+'-pubmin').value = "90";
      form.querySelector('#'+NS+'-power').value = "12";
      form.querySelector('#'+NS+'-load').value = "12";
      result.innerHTML = `<p class="${NS}-muted">Enter your site conditions and click “Estimate Runtime.”</p>`;
    });

    root.appendChild(wrap);
  }

  function init(){
    injectCSS();
    document.querySelectorAll(SELECTOR).forEach(root => {
      if (root.__CW_RT__) return;
      root.__CW_RT__ = true;
      build(root);
    });
  }

  if (document.readyState === "loading"){
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();